import { Component, inject, OnInit, signal } from '@angular/core';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { BookingService, EmployeeService, OnlineBookingService } from '@beautyapp/core';
import { AsyncPipe, DatePipe, DecimalPipe, NgClass } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgxPermissionsModule } from 'ngx-permissions';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { TranslateModule } from '@ngx-translate/core';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { format } from 'date-fns';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { map, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';
import { NzMessageService } from 'ng-zorro-antd/message';
import { DatepickerComponent } from '../../../../../form/src/lib/datepicker/datepicker.component';
import { TimePickerComponent } from '../../../../../form/src/lib/time-picker/time-picker.component';
import { SelectComponent } from '../../../../../form/src/lib/select/select.component';
import { ServiceRescheduleDrawerComponent } from './service-reschedule-drawer/service-reschedule-drawer.component';

@Component({
  selector: 'booking-hub-online-booking-modal',
  templateUrl: './online-booking-modal.component.html',
  styleUrls: ['./online-booking-modal.component.scss'],
  imports: [
    AsyncPipe,
    DecimalPipe,
    FormsModule,
    NgxPermissionsModule,
    NzButtonModule,
    NzGridModule,
    NzPopconfirmModule,
    ReactiveFormsModule,
    TranslateModule,
    NzModalModule,
    NzSpinModule,
    DatepickerComponent,
    NzTypographyModule,
    TimePickerComponent,
    NzCheckboxModule,
    SelectComponent,
    NzSpaceModule,
    NzDividerModule,
    NgClass,
    NzCardModule,
    NzTagModule,
    NzIconModule,
    NzToolTipModule,
    NzRadioModule,
    NzInputModule,
    NzAlertModule,
    NzStatisticModule,
    DatePipe
  ]
})
export class OnlineBookingModalComponent implements OnInit {
  readonly data = inject(NZ_MODAL_DATA);

  modalRef = inject(NzModalRef);

  onlineBookingService = inject(OnlineBookingService);
  employeeService = inject(EmployeeService);
  bookingService = inject(BookingService);
  drawerService = inject(NzDrawerService);
  messageService = inject(NzMessageService);

  onlineBooking = signal<any>(null);
  employeeBusyTimes = signal<any[]>([]);
  loading = signal<boolean>(false);

  // Reject form state
  showRejectForm = signal(false);
  selectedReason = signal<string | null>(null);
  customMessage = signal('');

  rejectionReasons = [
    { label: 'დრო დაკავებულია', value: 'დრო დაკავებულია' },
    { label: 'თანამშრომელი დაკავებულია', value: 'თანამშრომელი დაკავებულია' },
    { label: 'სერვისი მიუწვდომელია', value: 'სერვისი მიუწვდომელია' },
    { label: 'სხვა', value: 'სხვა' },
  ];

  form = new FormGroup({
    bookingDate: new FormControl<string | null>(null, Validators.required),
    startTime: new FormControl<Date | null>(null, Validators.required),
    endTime: new FormControl<Date | null>(null, Validators.required),
    description: new FormControl(null, Validators.required),
    employeeId: new FormControl(null, Validators.required),
    userId: new FormControl(null, Validators.required),
    services: new FormControl(null, Validators.required)
  });

  employee$: Observable<any> = this.employeeService.getDropdown().pipe(
    switchMap((res) => {
      return of(
        res.map((m: any) => ({
          ...m,
          fullName: m.user?.fullName || ''
        }))
      );
    })
  );

  ngOnInit() {
    this.getOnlineBooking();
  }

  getOnlineBooking() {
    this.onlineBookingService.getById(this.data.id).subscribe((res) => {
      this.onlineBooking.set(res);

      const startTime = new Date(this.onlineBooking().bookingDate);
      startTime.setHours(this.onlineBooking().startTime.split(':')[0], this.onlineBooking().startTime.split(':')[1]);

      const endTime = new Date(this.onlineBooking().bookingDate);
      endTime.setHours(this.onlineBooking().endTime.split(':')[0], this.onlineBooking().endTime.split(':')[1]);

      this.form.patchValue({
        bookingDate: format(new Date(this.onlineBooking().bookingDate), 'yyyy-MM-dd'),
        startTime: startTime,
        endTime: endTime,
        description: this.onlineBooking().description,
        employeeId: this.onlineBooking().employeeId,
        userId: this.onlineBooking().userId,
        services: this.onlineBooking().services
      });

      this.getEmployeeBookings();
    });
  }

  getEmployeeBookings() {
    const { bookingDate } = this.form.value;

    if (!bookingDate) {
      return;
    }

    this.bookingService
      .employeeBusyTimes({
        date: format(new Date(bookingDate), 'yyyy-MM-dd'),
        employeeId: this.form?.value?.employeeId
      })
      .pipe(
        map((res) => {
          return res.map((m: any) => ({
            ...m,
            isBusy: this.checkBusyTime(m.startTime, m.endTime)
          }));
        })
      )
      .subscribe((res) => {
        this.employeeBusyTimes.set(res);
      });
  }


  checkBusyTime(start: string, end: string) {
    const { startTime, endTime, bookingDate } = this.form.value;

    if (!startTime || !endTime || !bookingDate) {
      return false;
    }

    const startTimeDate = new Date(bookingDate);
    startTimeDate.setHours(+start.split(':')[0], +start.split(':')[1]);

    const endTimeDate = new Date(bookingDate);
    endTimeDate.setHours(+end.split(':')[0], +end.split(':')[1]);

    return startTimeDate <= endTime && endTimeDate >= startTime;
  }

  accept() {
    this.loading.set(true);
    this.onlineBookingService.accept(this.data.id).subscribe({
      next: () => {
        this.messageService.success('ჯავშანი წარმატებით დადასტურდა');
        this.modalRef.triggerOk().then(() => {
          this.loading.set(false);
        });
      },
      error: (err) => {
        this.loading.set(false);
        this.messageService.error(err?.error?.message || 'დადასტურება ვერ მოხერხდა');
      }
    });
  }

  showRejectDialog() {
    this.showRejectForm.set(true);
  }

  cancelReject() {
    this.showRejectForm.set(false);
    this.selectedReason.set(null);
    this.customMessage.set('');
  }

  confirmReject() {
    const reason = this.selectedReason();
    if (!reason) {
      this.messageService.warning('გთხოვთ აირჩიოთ უარყოფის მიზეზი');
      return;
    }

    this.loading.set(true);
    const customMsg = reason === 'სხვა' ? this.customMessage() : undefined;

    this.onlineBookingService.reject(this.data.id, reason, customMsg).subscribe({
      next: () => {
        this.messageService.success('ჯავშანი უარყოფილია');
        this.modalRef.triggerOk().then(() => {
          this.loading.set(false);
        });
      },
      error: (err) => {
        this.loading.set(false);
        this.messageService.error(err?.error?.message || 'უარყოფა ვერ მოხერხდა');
      }
    });
  }

  /**
   * Check if booking uses per-service scheduling (from BookingHub)
   */
  hasPerServiceScheduling(): boolean {
    const services = this.onlineBooking()?.services || [];
    return services.some((s: any) => s.staffReferenceId || s.bookingDate || s.startTime);
  }

  /**
   * Format service date for display (YYYY-MM-DD to dd/MM/yyyy)
   */
  formatServiceDate(dateStr: string): string {
    if (!dateStr) return '';
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy');
    } catch {
      return dateStr;
    }
  }

  /**
   * Format service time for display
   * Handles both "HH:mm" and "yyyy-MM-dd HH:mm" formats
   */
  formatServiceTime(timeStr: string): string {
    if (!timeStr) return '';
    // If format is "yyyy-MM-dd HH:mm", extract just the time
    if (timeStr.includes(' ')) {
      return timeStr.split(' ')[1];
    }
    return timeStr;
  }

  /**
   * Open reschedule drawer for a specific service
   */
  openRescheduleDrawer(service: any, index: number) {
    const drawerRef = this.drawerService.create({
      nzTitle: `გადანიშვნა: ${service.name}`,
      nzWidth: '80%',
      nzContent: ServiceRescheduleDrawerComponent,
      nzData: {
        service: service,
        serviceIndex: index,
        onlineBookingId: this.data.id,
        employeeId: service.staffReferenceId || this.form.value.employeeId,
        currentDate: service.bookingDate,
        currentStartTime: service.startTime,
        currentEndTime: service.endTime,
        serviceDuration: service.duration || service.time || 60
      }
    });

    drawerRef.afterClose.subscribe((result) => {
      if (result) {
        // Refresh the online booking data
        this.getOnlineBooking();
      }
    });
  }
}
